import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { Common } from '../lib/common-methods';
import { ReservationService } from '../lib/reservation.service';
import { UtilsService } from '../lib/utils.service';
import { IRefreshableComponent } from '../model/refreshable-component.interface';
import { Reservation, ReservationListFilters, Shift, ShiftType, SortType } from '../model/reservation';
import * as ReservationListActions from '../store/actions/reservation-list.actions';
import * as ReservationListSelectors from '../store/selectors/reservation-list.selectors';
import * as AppSelectors from '../store/selectors/app.selectors';
import moment from 'moment';
import { ReservationTagCategory } from '../model/reservation-tag';
import * as AppActions from './../store/actions/app.actions';
import { environment } from 'src/environments/environment';
import { CapacitorStorageService } from '../lib/capacitor-storage.service';
import { FontSize } from '../settings-form/settings-form.component';
import { ModalDialogComponent } from 'src/app/modal-dialog/modal-dialog.component';
import { ReservationApiService } from '../apis/reservation-api.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { OnlineReservationCategory } from '@model/login-response.dto';

@Component({
  selector: 'app-reservation-list',
  templateUrl: './reservation-list.component.html',
  styleUrls: ['./reservation-list.component.scss']
})
export class ReservationListComponent implements OnInit, AfterViewInit, OnDestroy, IRefreshableComponent {

  constructor(
    private store: Store<{ reservations: Reservation[] }>,
    private reservationService: ReservationService,
    private reservationApiService: ReservationApiService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private capacitorStorageService: CapacitorStorageService,
    private spinner: NgxSpinnerService,
    private elementRef: ElementRef
  ) { }

  @HostBinding('class.compact-view') get compactViewClass() { return this.compactView; }
  @ViewChild('manageAvailabilityModal') manageAvailabilityModal!: ModalDialogComponent;

  @HostListener('scroll', ['$event.target'])
  onScroll(target: Element) {
    const { scrollTop } = target;
    this.store.dispatch({ type: ReservationListActions.setScrollTop.type, scrollTop });

  };

  destroy$: Subject<boolean> = new Subject<boolean>();

  reservations$: Observable<Reservation[]> = this.store.pipe(select(ReservationListSelectors.selectAllReservations));
  reservations: Reservation[] = [];
  processedShifts$: Observable<Shift[]> = this.store.pipe(select(ReservationListSelectors.selectProcessedShifts));
  expandedReservationIds$: Observable<string[]> = this.store.pipe(select(ReservationListSelectors.expandedReservationIds));
  restaurantName$: Observable<string> = this.store.pipe(select(AppSelectors.selectRestaurantName));
  restaurantId$: Observable<number | undefined> = this.store.pipe(select(AppSelectors.selectRestaurantId));
  isOffline$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectIsOffline));
  isOffline: boolean = false;
  restaurantHasExtraInfo$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasExtraInfo));
  sortType$: Observable<SortType> = this.store.pipe(select(ReservationListSelectors.selectSortType));
  sortDesc$: Observable<boolean> = this.store.pipe(select(ReservationListSelectors.selectSortDesc));
  filters$: Observable<ReservationListFilters> = this.store.pipe(select(ReservationListSelectors.selectFilters));
  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedShift: Shift | null | undefined;
  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  compactView$: Observable<boolean | null> = this.store.pipe(select(ReservationListSelectors.selectCompactView));
  compactView: boolean | null = null;
  date$: Observable<Date> = this.store.pipe(select(ReservationListSelectors.selectDate));
  date: Date = new Date();
  reservationListFontSize$: Observable<FontSize> = this.store.pipe(select(AppSelectors.selectReservationListFontSize));
  reservationListFontSize: FontSize = FontSize.Default;
  restaurantHasOnlineCategories$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasOnlineCategories));
  restaurantHasOnlineCategories: boolean = false;
  visibleOnlineCategories$: Observable<OnlineReservationCategory[]> = this.store.pipe(select(AppSelectors.selectVisibleOnlineCategories));
  visibleOnlineCategories: OnlineReservationCategory[] = [];
  scrollTop$: Observable<number> = this.store.pipe(select(ReservationListSelectors.selectScrollTop));
  scrollTop: number = 0;
  dietaryTagCategoryIds: string[] = [];
  filteredReservations: Reservation[] = [];
  allTags: ReservationTagCategory[] = [];
  blocking: {
    totalCovers: number,
    totalRes: number,
    pendingRes: number,
    pendingCovers: number
  } = {
      totalCovers: 0,
      totalRes: 0,
      pendingRes: 0,
      pendingCovers: 0
    };

  sortType: string = SortType.Time.toString();
  sortTypes: { value: string; display: any; }[] = [];
  sortDesc: boolean = false;

  searchTerm: string = '';

  filters: ReservationListFilters = {
    groupByStatus: false,
    withDietary: false,
    withType: false,
    withResvTags: false,
    withNotes: false,
    withDiscount: false,
    withBill: false,
    withCake: false,
    withSource: false,
    withFood: false,
    withDrink: false,
    withTokenization: false,
    //withDeposit: false,
    reservationCategories: [],
  };

  isDayAvailable: boolean = false;
  workingDate: string = "";
  workingDateName: string = "";
  workingDateId: string | null = null;
  uncheckedLabel: string = "";
  checkedLabel: string = "";
  errorMessage: string = '';
  successMessage: string = '';

  lang: string = 'en';

  async ngOnInit(): Promise<void> {

    this.capacitorStorageService.setLatestActiveView('ReservationsList');

    this.lang = this.translate.currentLang;

    this.refresh();

    this.reservations$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.reservations = data;
      this.refreshFilteredReservations();

      if (this.allTags != null && this.allTags.length > 0) {
        for (let reservation of this.reservations) {
          if (environment.showTags && ((reservation?.reservationDetails?.reservationTags ?? []).some(tag => !this.allTags.some(category => category.id === tag.categoryId))
            || (reservation?.customerDetails?.customerTags ?? []).some(tag => !this.allTags.some(category => category.id === tag.categoryId)))) {
            // fetch tags
            this.store.dispatch({ type: AppActions.loadTags.type });
            break;
          }
        }
      }

    });

    this.selectedShift = await firstValueFrom(this.selectedShift$);

    this.processedShifts$.pipe(takeUntil(this.destroy$)).subscribe((shifts) => {

      if (shifts == null || shifts.length <= 0) {
        return;
      }

      let shiftToSelect: Shift | null = null;

      if (this.selectedShift != null && !shifts.some(s => s.shiftId === this.selectedShift?.shiftId)) {

        // select the shift of the same day part if exists
        const sameDayPartShift = shifts.find(s => s.type === this.selectedShift?.type);
        if (sameDayPartShift != null) {
          shiftToSelect = sameDayPartShift;
        } else {
          shiftToSelect = shifts[0];
        }

      } else if (this.selectedShift == null) {
        shiftToSelect = shifts[0];
      }

      if (shiftToSelect == null) {
        return;
      }

      this.selectShift(shiftToSelect);
    });


    const restaurantHasExtraInfo = await firstValueFrom(this.restaurantHasExtraInfo$);

    this.sortTypes = (Object.keys(SortType)
      .filter((key: any) => {

        // if (!restaurantHasExtraInfo && parseInt(SortType[key]) === SortType.ExtraInfo) {
        //   return false;
        // }

        return !isNaN(Number(SortType[key]));
      })
      .map((key: any) => {
        return { value: SortType[key], display: this.translate.instant(`reservations.list.${key.toString().toLowerCase()}`) };
      }));

    this.sortType$.pipe(takeUntil(this.destroy$)).subscribe((sortType) => {
      this.sortType = sortType.toString();
    });

    this.sortDesc$.pipe(takeUntil(this.destroy$)).subscribe((sortDesc) => {
      this.sortDesc = sortDesc;
    });

    this.filters$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
      this.filters = filters;
      this.refreshFilteredReservations();
    });

    this.selectedShift$.pipe(takeUntil(this.destroy$)).subscribe((shift) => {
      this.selectedShift = shift;
      this.refreshFilteredReservations();
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.allTags = tags ?? [];
      this.dietaryTagCategoryIds = (tags ?? []).filter(t => t.isDietary()).map(t => t.id);
    });

    this.isOffline$.pipe(takeUntil(this.destroy$)).subscribe((isOffline) => {
      this.isOffline = isOffline;
    });

    this.compactView$.pipe(takeUntil(this.destroy$)).subscribe((compactView) => {
      this.compactView = compactView;
    });

    this.date$.pipe(takeUntil(this.destroy$)).subscribe((date) => {
      this.date = date;
    });

    this.reservationListFontSize$.pipe(takeUntil(this.destroy$)).subscribe((reservationListFontSize) => {
      this.reservationListFontSize = reservationListFontSize;
    });

    this.visibleOnlineCategories$.pipe(takeUntil(this.destroy$)).subscribe((onlineCategories) => {
      this.visibleOnlineCategories = onlineCategories;
    });
    
    this.restaurantHasOnlineCategories$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasOnlineCategories) => {
      this.restaurantHasOnlineCategories = restaurantHasOnlineCategories;
    });

    this.scrollTop$.pipe(takeUntil(this.destroy$)).subscribe((scrollTop) => {
      this.scrollTop = scrollTop;

      if (this.elementRef.nativeElement.scrollTop !== scrollTop) {
        setTimeout(() => {
          this.setScrollTop();
        }, 10);
      }
    });

    this.uncheckedLabel = this.translate.instant('manageAvailability.closed');
    this.checkedLabel = this.translate.instant('manageAvailability.opened');
  }

  ngAfterViewInit(): void {
    this.setScrollTop();
  }

  setScrollTop() {
    if (this.scrollTop <= 0 || this.elementRef.nativeElement == null) {
      return;
    }

    this.elementRef.nativeElement.scrollTo(0, this.scrollTop);
  }

  refresh(): Promise<void> {
    // load reservations
    this.store.dispatch({ type: ReservationListActions.loadReservations.type });
    return Promise.resolve();
  }

  filterReservation(reservation: Reservation) {

    // const searchTerm: string = (Common.removePunctuation(args[0][1]) ?? '').toLowerCase();

    if (!Common.timeBelongsToShift(reservation.reservationDetails.arrivalTime, this.selectedShift)) {
      return false;
    }

    // no filter selected
    if (this.getSelectedFiltersNumber(true) <= 0) {
      return true;
    }

    // with dietary tags
    if (this.filters.withDietary === true) {
      if ((reservation.customerDetails.allergy != null && reservation.customerDetails.allergy.trim().length > 0)
        || (reservation.reservationDetails.allergy != null && reservation.reservationDetails.allergy.trim().length > 0)
        || (reservation.reservationDetails.reservationTags?.some(tag => this.dietaryTagCategoryIds.includes(tag.categoryId)))) {
        return true;
      }
    }

    // with type (all except 'Simple')
    if (this.filters.withType === true) {
      if (reservation.reservationDetails.reservationType != null && reservation.reservationDetails.reservationType !== 'S') {
        return true;
      }
    }

    // todo: uncomment
    // with reservation tags
    // if (filters.withResvTags === true) {
    //   if (reservation.reservationDetails.reservationTags == null || reservation.reservationDetails.reservationTags.length <= 0) {
    //     return true;
    //   }
    // }

    // with notes
    if (this.filters.withNotes === true) {
      if (reservation.reservationDetails.notes != null && reservation.reservationDetails.notes.trim().length > 0) {
        return true;
      }
    }

    // with discount
    if (this.filters.withDiscount === true) {
      if (!Common.isNullOrZero(reservation.reservationDetails.discount)) {
        return true;
      }
    }

    // with bill
    if (this.filters.withBill === true) {
      if (!Common.isNullOrZero(reservation.reservationDetails.bill)) {
        return true;
      }
    }

    // with tokenization
    if (this.filters.withTokenization === true) {
      if (reservation.reservationDetails.tokenization === true) {
        return true;
      }
    }

    // with deposit
    /*if (this.filters.withDeposit === true) {
      if (reservation.reservationDetails.deposit === true) {
        return true;
      }
    }*/

    // with cake
    if (this.filters.withCake === true) {
      if (reservation.reservationDetails.hasCake === true) {
        return true;
      }
    }

    // with source
    if (this.filters.withSource === true) {
      if (reservation.sourceDetails != null &&
        reservation.sourceDetails.id != null &&
        reservation.sourceDetails.id.trim() !== "") {
        return true;
      }
    }

    // with food
    if (this.filters.withFood === true) {
      if (reservation.reservationDetails.purpose === 'Food') {
        return true;
      }
    }

    // with drink
    if (this.filters.withDrink === true) {
      if (reservation.reservationDetails.purpose === 'Drink') {
        return true;
      }
    }

    // if (searchTerm == null || searchTerm.trim().length <= 0) {
    //   return true;
    // }

    // return (Common.removePunctuation(reservation.customerDetails?.name ?? '')?.toLowerCase().includes(searchTerm)
    //   || Common.removePunctuation(reservation.customerDetails?.surname ?? '')?.toLowerCase().includes(searchTerm)
    //   || Common.removePunctuation(reservation.reservationDetails?.allocatedTables)?.toLowerCase().includes(searchTerm)) ?? false;

    if (this.filters.reservationCategories.length > 0) {
      if (reservation.reservationDetails?.onlineCategory?.id != null
        && this.filters.reservationCategories.indexOf(reservation.reservationDetails.onlineCategory?.id) >= 0) {
        return true;
      }
    }

    return false;
  }

  initReservationsSorting(reservations: Reservation[]) {
    return Common.initialSortReservations(reservations);
  }

  setSortType(sortType: any) { // sortType: SortType
    if (sortType == null || sortType === '') {
      return;
    }
    this.store.dispatch({ type: ReservationListActions.setSortType.type, sortType });
  }

  toggleSorting() {
    this.store.dispatch({ type: ReservationListActions.setSortDesc.type, sortDesc: !this.sortDesc });
  }

  sortReservations(reservationA: Reservation, reservationB: Reservation, ...args: any[]): -1 | 0 | 1 {

    const sortType: SortType = parseInt(args[0][0]);
    const sortDesc: boolean = args[0][1];

    switch (sortType) {
      case SortType.Surname:

        let nameA = reservationA.customerDetails.surname;
        let nameB = reservationB.customerDetails.surname;

        if (nameA == null && nameB == null) {
          nameA = reservationA.customerDetails.name;
          nameB = reservationB.customerDetails.name;
        }

        if (nameA != null) {
          nameA = nameA.toLowerCase();
        }

        if (nameB != null) {
          nameB = nameB.toLowerCase();
        }

        nameA = nameA ?? '';
        nameB = nameB ?? '';

        if (nameA > nameB) {
          return sortDesc ? -1 : 1;
        }

        if (nameA < nameB) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      case SortType.Time:

        const timeA = moment(reservationA.reservationDetails.arrivalTime);
        const timeB = moment(reservationB.reservationDetails.arrivalTime);

        if (timeA.isAfter(timeB)) {
          return sortDesc ? -1 : 1;
        }

        if (timeA.isBefore(timeB)) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      case SortType.Table:

        const reservationATables = reservationA.reservationDetails?.allocatedTables?.split(',')
          .filter(table => table != null && table.trim().length > 0)
          .sort((a, b) => {
            return Common.compareTableNames(a, b, sortDesc);
          });

        const reservationBTables = reservationB.reservationDetails?.allocatedTables?.split(',')
          .filter(table => table != null && table.trim().length > 0)
          .sort((a, b) => {
            return Common.compareTableNames(a, b, sortDesc);
          });

        let tableA = (reservationATables ?? [])[0];
        let tableB = (reservationBTables ?? [])[0];
        let i = 1;
        while (tableA != null && tableB != null && tableA === tableB) {
          tableA = (reservationATables ?? [])[i];
          tableB = (reservationBTables ?? [])[i];
          i++;
        }

        return Common.compareTableNames(tableA, tableB, sortDesc);
      case SortType.Cover:

        const coverA = reservationA.reservationDetails.covers;
        const coverB = reservationB.reservationDetails.covers;

        if (coverA > coverB) {
          return sortDesc ? -1 : 1;
        }

        if (coverA < coverB) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      // case SortType.ExtraInfo:

      //   const extraInfoA = reservationA.reservationDetails?.extraInfo ?? '';
      //   const extraInfoB = reservationB.reservationDetails?.extraInfo ?? '';

      //   if (extraInfoA > extraInfoB) {
      //     return sortDesc ? -1 : 1;
      //   }

      //   if (extraInfoA < extraInfoB) {
      //     return sortDesc ? 1 : -1;
      //   }

      //   return 0;
      case SortType.Status:

        const statusA = Common.getStatusIndex(reservationA.reservationDetails?.reservationsStatus);
        const statusB = Common.getStatusIndex(reservationB.reservationDetails?.reservationsStatus);

        if (statusA > statusB) {
          return sortDesc ? -1 : 1;
        }

        if (statusA < statusB) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      case SortType.CreatedAt:

        const createdTimeA = moment(reservationA.reservationDetails.creationTime);
        const createdTimeB = moment(reservationB.reservationDetails.creationTime);

        if (createdTimeA.isAfter(createdTimeB)) {
          return sortDesc ? -1 : 1;
        }

        if (createdTimeA.isBefore(createdTimeB)) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      // case SortType.Source:

      //   const sourceA = reservationA.sourceDetails?.sourceType ?? '';
      //   const sourceB = reservationB.sourceDetails?.sourceType ?? '';

      //   if (sourceA > sourceB) {
      //     return sortDesc ? -1 : 1;
      //   }

      //   if (sourceA < sourceB) {
      //     return sortDesc ? 1 : -1;
      //   }

      //   return 0;
      case SortType.Category:

        const categoryA = (reservationA.reservationDetails.onlineCategory?.iconCaption ?? '').toLowerCase();
        const categoryB = (reservationB.reservationDetails.onlineCategory?.iconCaption ?? '').toLowerCase();

        if (categoryA > categoryB) {
          return sortDesc ? -1 : 1;
        }

        if (categoryA < categoryB) {
          return sortDesc ? 1 : -1;
        }

        return Common.arrivalTimeComparator(reservationA, reservationB);
      default:
        return Common.arrivalTimeComparator(reservationA, reservationB);
    }
  }

  toggleCategoryFilter(category: OnlineReservationCategory) {

    if (category.id == null) {
      return;
    }

    if (this.filters.reservationCategories.some(c => c === category.id)) {
      this.setFilters({ reservationCategories: this.filters.reservationCategories.filter(c => c !== category.id) });
      return;
    }

    this.setFilters({ reservationCategories: this.filters.reservationCategories.concat(category.id as string) });
  }

  setFilters(patch: Partial<ReservationListFilters>) {
    this.store.dispatch({ type: ReservationListActions.setFilters.type, filters: { ...this.filters, ...patch } });
  }

  selectShift(shift: Shift) {
    this.store.dispatch({ type: ReservationListActions.setSelectedShift.type, shift });
  }

  refreshFilteredReservations() {
    this.filteredReservations = this.reservations.filter(reservation => {
      return this.filterReservation(reservation);
    });
    this.blocking = this.utilsService.calculateBlocking(this.filteredReservations, true);
  }

  getReservationsForExport(): Reservation[] {
    const reservations = [...this.filteredReservations];

    return reservations.sort((a, b) => {
      return this.sortReservations(a, b, [[this.sortType, this.sortDesc]]);
    });
  }

  resetFilters() {
    this.filters = {
      groupByStatus: false,
      withDietary: false,
      withType: false,
      withResvTags: false,
      withNotes: false,
      withDiscount: false,
      withBill: false,
      withCake: false,
      withSource: false,
      withFood: false,
      withDrink: false,
      withTokenization: false,
      //withDeposit: false,
      reservationCategories: [],
    };
    this.store.dispatch({ type: ReservationListActions.setFilters.type, filters: { ...this.filters } });
    //this.refreshFilteredReservations();
  }

  getSelectedFiltersNumber(excludeGroupByFilters: boolean = false) {
    if (!excludeGroupByFilters) {
      return Object.values(this.filters).filter(value => value === true).length + (this.filters?.reservationCategories ?? []).length;
    }

    return Object.entries(this.filters).filter((entry) => entry[0] !== 'groupByStatus' && entry[1] === true).length + (this.filters?.reservationCategories ?? []).length;
  }

  getShiftTitle(shiftType: ShiftType | undefined) {
    return this.reservationService.translateShiftTitle(shiftType);
  }

  async toggleReservation(reservation: Reservation) {
    const expandedReservationIds = await firstValueFrom(this.expandedReservationIds$);

    const reservationId = reservation.reservationDetails.reservationId as string;
    if (!expandedReservationIds.includes(reservationId)) {
      this.store.dispatch({ type: ReservationListActions.expandReservation.type, reservationId });
    } else {
      this.store.dispatch({ type: ReservationListActions.collapseReservation.type, reservationId });
    }
  }

  setGlobalSearchTerm(event: Event) {
    const term = (event.target as HTMLInputElement).value;
    this.store.dispatch({ type: ReservationListActions.setGlobalSearchTerm.type, term });
  }

  canSearchReservation(): boolean {
    return this.utilsService.canWrite('SearchReservation');
  }

  canReadCustomer(): boolean {
    return this.utilsService.canRead('Type') || this.utilsService.canRead('FullName');
  }

  async toggleCompactView() {
    this.store.dispatch({ type: ReservationListActions.setCompactView.type, value: !this.compactView });
  }

  today() {
    this.store.dispatch({ type: ReservationListActions.setDate.type, date: new Date() });
  }

  isTodaySelected() {
    const moment1 = moment(this.date);
    const moment2 = moment(new Date());
    return moment1.isSame(moment2, 'date');
  }

  get showAvailabilityButton(): boolean {
    return this.utilsService.canWrite('QuickCloseAvailability');
  }

  async showManageAvailability() {
    this.spinner.show();
    this.errorMessage = '';
    this.successMessage = '';
    this.workingDateId = null;

    this.workingDate = moment(this.date).format('DD-MM-YYYY');
    this.workingDateName = 'weekDays.' + moment(this.date).format('dddd');

    const restaurantId = await firstValueFrom(this.restaurantId$);

    if (restaurantId) {
      const payload = {
        restaurantId: restaurantId.toString(),
        workingDate: moment(this.date).format('YYYY-MM-DD'),
        workingDateName: moment(this.date).format('dddd')
      };

      this.reservationApiService.getAvailability(payload).subscribe(response => {
        this.isDayAvailable = response.hasOnlineAvailability;
        this.workingDateId = response.workingDateId;
        if (!response.Status) {
          this.errorMessage = this.translate.instant('manageAvailability.retriveErrorMessage');
        }
        if (!response.workingDateId) {
          this.errorMessage = this.translate.instant('manageAvailability.noOnlineAvailability');
        }
        this.spinner.hide();
        this.manageAvailabilityModal.show();
      }, error => {
        this.spinner.hide();
        this.manageAvailabilityModal.show();
        this.errorMessage = this.translate.instant('manageAvailability.retriveErrorMessage');
      });
    }
  }

  async onlineAvailabilityChange(event: Event) {
    this.errorMessage = '';
    this.successMessage = '';
    const isChecked = (event.target as HTMLInputElement).checked;
    const restaurantId = await firstValueFrom(this.restaurantId$);
    if (restaurantId) {
      const payload = {
        restaurantId: restaurantId.toString(),
        workingDateId: this.workingDateId,
        workingDate: moment(this.date).format('YYYY-MM-DD'),
        workingDateName: moment(this.date).format('dddd'),
        close: !isChecked
      };
      this.reservationApiService.updateAvailability(payload).subscribe(response => {
        if (!response.Status) {
          this.errorMessage = this.translate.instant('manageAvailability.updateFailedMessage');
        } else if (!response.workingDateId) {
          this.errorMessage = this.translate.instant('manageAvailability.noOnlineAvailability');
        } else {
          this.isDayAvailable = response.hasOnlineAvailability;
          this.workingDateId = response.workingDateId;
          this.successMessage = this.translate.instant('manageAvailability.updateSuccessMessage');
        }
      }, error => {
        this.errorMessage = this.translate.instant('manageAvailability.updateFailedMessage');
      });
    }
  }


  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
